import { useState } from "react";

import {
  getMyReviewState,
  postConnectionCheckin,
  type CheckinResponse,
  type MyReviewState,
} from "../../lib/marketplace-connection-api";
import type { CheckinCardDTO } from "../../lib/marketplace-status";
import {
  reviewAskView,
  reviewPostedMessage,
  reviewWriteFailure,
  submitReviewAnswer,
  type ReviewAnswer,
  type ReviewWriteOutcome,
} from "../../lib/review-ask";
import { Button } from "../ui/button";
import {
  DisconnectMarketplaceDialog,
  type DisconnectParams,
} from "./DisconnectMarketplaceDialog";
import { ReviewAskDialog } from "./ReviewAskDialog";

/**
 * CheckinCard — the buyer-facing M8-C check-in surface (plan 292 cycle 5,
 * spec §3, artboard 3c, D11 generous-tap window).
 *
 * ONE TAP, the ask persists, and it NEVER expires or shows a countdown —
 * mirroring the backend's own contract: `checkin.pending` stays true across
 * the day-0/7/30 reminder ladder until the buyer actually answers (see
 * `relationship.Service.CheckinCardState`'s doc comment). This component
 * renders nothing time-boxed: no "expires in", no progress bar, no
 * urgency-toned copy.
 *
 * Both variants (`close_claim` / `light`) ask the same three one-tap
 * questions — only the headline framing differs. Answers:
 *   - closed          → celebratory thanks state (NEVER promises a review
 *                       ask — that's M11's surface to build).
 *   - still_looking   → quiet, wordless ack; the card steps down to a
 *                       one-line acknowledgment with no further CTA.
 *   - parted          → opens the EXISTING DisconnectMarketplaceDialog via
 *                       the `next: "disconnect"` affordance hint. This
 *                       component never auto-disconnects — the dialog's own
 *                       confirm→reason→review flow owns everything from
 *                       there.
 *
 * Self-contained by design (no relationship-hub dependency) so the M8-A
 * hub (a different branch) can re-mount this component unchanged at merge.
 */

export interface CheckinCardProps {
  checkin: CheckinCardDTO;
  connectionId: string;
  /** The connection's own role — threaded through to the checkin POST and
   *  the disconnect POST exactly like `useMarketplaceConnection`'s read. */
  role: string;
  /** Display name for the connected pro — absent falls back to neutral
   *  "your agent" copy. */
  agentName?: string;
  getToken: () => Promise<string | null>;
  /** Called after a `closed` or `still_looking` answer lands successfully —
   *  lets the caller refresh the connection read so `checkin.pending`
   *  reflects the server's new state on the next render. Optional. */
  onAnswered?: (response: CheckinResponse) => void;
  /** Wired to the existing disconnect call
   *  (`disconnectMarketplaceConnection`) by the caller — mirrors
   *  `DisconnectMarketplaceDialogProps.onDisconnect` exactly so this
   *  component never needs its own disconnect implementation. */
  onDisconnect: (params: DisconnectParams) => Promise<void>;
  /**
   * Pro's own user id (M11-A, plan 320/322) — required to read the review
   * state and submit a review. After a "We closed" tap the card asks the
   * server what review ask remains (`next_ask`) and opens the close modal
   * only when it is `closed`: a buyer whose review is already closed is not
   * asked again. Absent renders the closed-thanks congrats state WITHOUT the
   * review ask — congrats-only, never a crash or a half-wired affordance.
   */
  proUserId?: string;
  /** Present only when this connection's accepted offer carried an
   *  incentive (rebate/cash/perk) — threaded straight to
   *  `ReviewCloseUpgradeModal`'s Yes/Partially/No "was it honored" chip.
   *  Absent renders no chip. */
  incentiveLabel?: string;
  /**
   * Preview/test seam only — seeds the card at a specific ANSWERED stage so
   * the DEV `/preview/offers?checkin=` fixture can screenshot every
   * post-answer state without wiring a live backend (mirrors
   * `DisconnectMarketplaceDialogProps.initialStep`'s idiom exactly).
   *
   * MUST NOT change real-user behavior: real callers never pass this — the
   * card always starts at `"asking"` and only reaches an answered stage via
   * an actual tap. Default: `"asking"`.
   */
  initialStage?: "asking" | "closed-thanks" | "still-looking-ack" | "parted-ack";
  /**
   * Preview/test seam only, paired with `initialStage="closed-thanks"`: the
   * review state a real "We closed" tap would have read from the server, so
   * the DEV fixture can show the close modal with no backend. Real callers
   * never pass it; the card reads the state itself after the tap.
   */
  initialReviewState?: MyReviewState;
}

type Stage =
  | { kind: "asking" }
  | { kind: "submitting"; response: CheckinResponse }
  | { kind: "closed-thanks" }
  | { kind: "still-looking-ack" }
  | { kind: "parted" }
  | { kind: "parted-ack" }
  | { kind: "error" };

const CLOSE_CLAIM_HEADLINE = "Did you close?";
const CLOSE_CLAIM_SUBTEXT = (agentCopy: string) =>
  `Let us know how things went with ${agentCopy}.`;

const LIGHT_HEADLINE = (agentCopy: string) =>
  `How's the search going with ${agentCopy}?`;
const LIGHT_SUBTEXT = "A quick update helps us support you.";

export const CHECKIN_CLOSED_THANKS_HEADLINE = "🎉 Congratulations!";
export const CHECKIN_CLOSED_THANKS_SUBTEXT =
  "Your agent has been told the great news.";
export const CHECKIN_STILL_LOOKING_ACK = "Thanks for the update.";
export const CHECKIN_PARTED_ACK = "Got it — thanks for letting us know.";

export function CheckinCard({
  checkin,
  connectionId,
  role,
  agentName,
  getToken,
  onAnswered,
  onDisconnect,
  proUserId,
  incentiveLabel,
  initialStage = "asking",
  initialReviewState,
}: CheckinCardProps) {
  const [stage, setStage] = useState<Stage>({ kind: initialStage });
  // M11-A: whether the buyer has dismissed the post-close review ask.
  // Dismissing never re-prompts — no cross-render "already dismissed but
  // still nagging" state, and submitting also settles here so the modal
  // never lingers open after a successful send.
  const [reviewDismissed, setReviewDismissed] = useState(false);
  // The server's review state, read after the close tap lands (the tap is
  // what arms the closed ask). Undefined = not read / read failed: no modal.
  const [reviewState, setReviewState] = useState<MyReviewState | undefined>(initialReviewState);
  const [reviewNotice, setReviewNotice] = useState<ReviewWriteOutcome | null>(null);

  if (!checkin.pending && stage.kind === "asking") {
    // Nothing to ask — the caller should have gated mounting on
    // `checkin.pending`, but stay defensive rather than render a stray card.
    return null;
  }

  const agentCopy = agentName ?? "your agent";
  const isLight = checkin.variant === "light";

  const answer = async (response: CheckinResponse) => {
    setStage({ kind: "submitting", response });
    try {
      const result = await postConnectionCheckin(getToken, role, response);
      onAnswered?.(response);
      if (response === "closed") {
        setStage({ kind: "closed-thanks" });
        if (proUserId) {
          // Best-effort: a failed read leaves the congrats without an ask,
          // never an ask the server did not offer.
          void getMyReviewState(getToken, proUserId)
            .then(setReviewState)
            .catch(() => setReviewState(undefined));
        }
      } else if (response === "still_looking") {
        setStage({ kind: "still-looking-ack" });
      } else if (result.next === "disconnect") {
        setStage({ kind: "parted" });
      } else {
        // Defensive fallback — the backend contract always returns
        // next:"disconnect" for a parted tap, but never leave the buyer on
        // a dead submitting state if that ever changes.
        setStage({ kind: "still-looking-ack" });
      }
    } catch {
      setStage({ kind: "error" });
    }
  };

  if (stage.kind === "closed-thanks") {
    const reviewView = reviewAskView(reviewState);
    const submitReview = async (answer: ReviewAnswer) => {
      setReviewDismissed(true);
      if (!proUserId || !reviewState) return;
      try {
        await submitReviewAnswer(getToken, proUserId, reviewState, answer);
        setReviewNotice({ kind: "posted", message: reviewPostedMessage(agentCopy) });
      } catch (error) {
        // The celebration stays; the buyer just sees why the review did not
        // post (e.g. "You've already reviewed …") instead of a silent drop.
        setReviewNotice(reviewWriteFailure(error, agentCopy));
      }
    };

    return (
      <div className="marketplace-surface p-5" data-testid="checkin-card">
        <div className="space-y-2" data-testid="checkin-closed-thanks">
          <p
            className="text-lg font-bold text-foreground"
            data-testid="checkin-closed-headline"
          >
            {CHECKIN_CLOSED_THANKS_HEADLINE}
          </p>
          <p
            className="text-sm text-muted-foreground"
            data-testid="checkin-closed-subtext"
          >
            {CHECKIN_CLOSED_THANKS_SUBTEXT}
          </p>
          {reviewNotice && (
            <p
              className={`text-sm ${reviewNotice.kind === "failed" ? "text-destructive" : "text-muted-foreground"}`}
              data-outcome={reviewNotice.kind}
              data-testid="checkin-review-notice"
              role="status"
            >
              {reviewNotice.message}
            </p>
          )}
        </div>
        {proUserId && reviewView.kind === "close" && !reviewDismissed && (
          <ReviewAskDialog
            congratsHeadline={CHECKIN_CLOSED_THANKS_HEADLINE}
            congratsSubtext={CHECKIN_CLOSED_THANKS_SUBTEXT}
            incentiveLabel={incentiveLabel}
            onClose={() => setReviewDismissed(true)}
            onSubmit={(answer) => void submitReview(answer)}
            open
            proName={agentCopy}
            view={reviewView}
          />
        )}
      </div>
    );
  }

  if (stage.kind === "still-looking-ack") {
    return (
      <div className="marketplace-surface p-4" data-testid="checkin-card">
        <p
          className="text-sm text-muted-foreground"
          data-testid="checkin-still-looking-ack"
        >
          {CHECKIN_STILL_LOOKING_ACK}
        </p>
      </div>
    );
  }

  if (stage.kind === "parted-ack") {
    return (
      <div className="marketplace-surface p-4" data-testid="checkin-card">
        <p
          className="text-sm text-muted-foreground"
          data-testid="checkin-parted-ack"
        >
          {CHECKIN_PARTED_ACK}
        </p>
      </div>
    );
  }

  return (
    <div className="marketplace-surface p-5" data-testid="checkin-card">
      {stage.kind !== "parted" && (
        <div className="space-y-3" data-testid="checkin-asking">
          <p
            className="text-base font-semibold text-foreground"
            data-testid="checkin-headline"
          >
            {isLight ? LIGHT_HEADLINE(agentCopy) : CLOSE_CLAIM_HEADLINE}
          </p>
          <p
            className="text-sm text-muted-foreground"
            data-testid="checkin-subtext"
          >
            {isLight ? LIGHT_SUBTEXT : CLOSE_CLAIM_SUBTEXT(agentCopy)}
          </p>

          {stage.kind === "error" && (
            <p className="text-sm text-destructive" data-testid="checkin-error">
              Couldn&rsquo;t save that — try again.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="checkin-answer-closed"
              disabled={stage.kind === "submitting"}
              loading={
                stage.kind === "submitting" && stage.response === "closed"
              }
              onClick={() => void answer("closed")}
            >
              🎉 We closed
            </Button>
            <Button
              data-testid="checkin-answer-still-looking"
              disabled={stage.kind === "submitting"}
              loading={
                stage.kind === "submitting" && stage.response === "still_looking"
              }
              onClick={() => void answer("still_looking")}
              variant="outline"
            >
              Still looking
            </Button>
            <Button
              data-testid="checkin-answer-parted"
              disabled={stage.kind === "submitting"}
              loading={
                stage.kind === "submitting" && stage.response === "parted"
              }
              onClick={() => void answer("parted")}
              variant="outline"
            >
              We parted ways
            </Button>
          </div>
        </div>
      )}

      {stage.kind === "parted" && (
        <DisconnectMarketplaceDialog
          agentName={agentCopy}
          connectionId={connectionId}
          onClose={() => setStage({ kind: "parted-ack" })}
          onDisconnect={onDisconnect}
          open
        />
      )}
    </div>
  );
}
